import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/i18n/LanguageContext";
import { usePortfolio } from "@/context/PortfolioContext";
import { Menu, X } from "lucide-react";

const EASE = [0.76, 0, 0.24, 1];

export default function Navbar() {
  const { t, lang, setLang } = useLang();
  const { visible: portfolioVisible } = usePortfolio();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const links = [
    { to: "/", label: t.nav.home, exact: true },
    { to: "/studio", label: t.nav.about },
    { to: "/services", label: t.nav.services },
    { to: "/industries", label: t.nav.industries },
    ...(portfolioVisible ? [{ to: "/portfolio", label: t.nav.portfolio }] : []),
    { to: "/process", label: t.nav.process },
  ];

  return (
    <motion.header
      data-testid="site-navbar"
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: EASE, delay: 0.2 }}
      className={[
        "fixed top-0 left-0 right-0 z-50",
        "backdrop-blur-xl",
        scrolled ? "bg-white/80 border-b border-[#E5E5EA]" : "bg-white/60 border-b border-transparent",
        "transition-[background-color,border-color] duration-500",
      ].join(" ")}
    >
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-3 group">
          <span className="w-8 h-8 border border-[#0A0A0A] flex items-center justify-center font-serif text-sm">
            <span className="leading-none">AZ</span>
          </span>
          <span className="hidden sm:flex flex-col leading-none">
            <span className="font-serif text-[15px] tracking-tight">AZ Mechanical Design</span>
            <span className="font-mono text-[9px] tracking-[0.22em] uppercase text-[#86868B] mt-1">Studio · IT / EN</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.exact}
              data-testid={`nav-link-${l.to.replace("/", "") || "home"}`}
              className={({ isActive }) => [
                "az-link font-sans text-[13px] tracking-wide text-[#0A0A0A]",
                isActive ? "az-link--active" : "",
              ].join(" ")}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-4 md:gap-6">
          <div className="az-lang" role="group" aria-label="Language">
            {["it", "en"].map((l, i) => (
              <React.Fragment key={l}>
                {i > 0 && <span aria-hidden="true">/</span>}
                <button
                  data-testid={`lang-switch-${l}`}
                  onClick={() => setLang(l)}
                  className={lang === l ? "is-active" : ""}
                >
                  {l.toUpperCase()}
                </button>
              </React.Fragment>
            ))}
          </div>

          <Link to="/contact" data-testid="navbar-cta" className="az-btn hidden md:inline-flex text-[12px]">
            <span>{t.nav.cta}</span>
            <span className="az-btn__arrow" aria-hidden="true">→</span>
          </Link>

          <button
            aria-label="Menu"
            data-testid="navbar-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden w-10 h-10 flex items-center justify-center border border-[#0A0A0A] rounded-full"
          >
            {open ? <X size={16} /> : <Menu size={16} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="lg:hidden overflow-hidden bg-white border-t border-[#E5E5EA]"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {links.concat([{ to: "/contact", label: t.nav.contact }]).map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-${l.to.replace("/", "") || "home"}`}
                  className="font-serif text-3xl tracking-tight"
                >
                  {l.label}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
